import { ReservationStatus } from '@/core/reservation';
import type { FlatTranslationKey } from '@/core/types/i18n';

export type PropertyTranslateFn = (key: FlatTranslationKey) => string;

export function getListingLabels(t: PropertyTranslateFn) {
  return {
    greetingPrefix: t('propertiesWeb.list.greetingPrefix'),
    exclusiveListings: t('home.exclusiveListingsForYou'),
    priceRange: t('properties.priceRange'),
    loanAmount: t('properties.loanAmount'),
    loanEligibility: t('properties.loanEligibility'),
    loanStatusAbove: t('properties.above'),
    loanStatusWithin: t('properties.within'),
    bedrooms: t('propertyDetails.bedrooms'),
    bathrooms: t('propertyDetails.bathrooms'),
    sqft: t('properties.sqFt'),
    handover: t('propertyDetails.handover'),
    aed: t('propertyDetails.aed'),
    millionAbbrev: t('propertiesWeb.list.millionAbbrev'),
    oneMillion: t('propertiesWeb.list.oneMillion'),
    twoMillion: t('propertiesWeb.list.twoMillion'),
    threeMillion: t('propertiesWeb.list.threeMillion'),
    sixMillion: t('propertiesWeb.list.sixMillion'),
    upcomingAppointment: t('bookAppointment.upcomingAppointment'),
    contactPersonName: t('propertyDetails.contactPersonName'),
    visitOffice: t('bookAppointment.visitOffice'),
    joinHere: t('bookAppointment.joinHere'),
    unitReserved: t('reserveUnit.unitReservedTitle'),
    viewReservation: t('propertyDetails.viewReservationCta'),
    today: t('bookAppointment.today'),
    tomorrow: t('bookAppointment.tomorrow'),
    cancel: t('propertyDetails.cancelAppointment'),
    reschedule: t('propertyDetails.reschedule'),
    bookAppointment: t('bookAppointment.bookAppointment'),
    emptyTitle: t('properties.noPropertiesYetTitle'),
    emptyNoProperties: t('propertiesWeb.list.emptyNoProperties'),
    emptyFilterNoMatch: t('propertiesWeb.list.emptyFilterNoMatch'),
    emptyFetchError: t('propertiesWeb.list.emptyFetchError'),
    projectDetailsTab: t('propertiesWeb.list.projectDetailsTab'),
    exploreUnitsTab: t('propertiesWeb.list.exploreUnitsTab'),
    exploreUnitsTitle: t('propertiesWeb.list.exploreUnitsTitle'),
    associatedUnits: t('propertiesWeb.list.associatedUnits'),
  } as const;
}

export type ListingLabels = ReturnType<typeof getListingLabels>;

function getReservationVisibleLabels(t: PropertyTranslateFn) {
  const inProgressListing = t('reserveUnit.reservationVisible.inProgressListing');
  const reservationFormSent = t(
    'reserveUnit.reservationVisible.reservationFormSent',
  );
  const awaitingYourSignature = t(
    'reserveUnit.reservationVisible.awaitingYourSignature',
  );
  const bookingFormSigned = t(
    'reserveUnit.reservationVisible.bookingFormSigned',
  );
  const reservationFormSigned = t(
    'reserveUnit.reservationVisible.reservationFormSigned',
  );
  const awaitingSpaSignature = t(
    'reserveUnit.reservationVisible.awaitingSpaSignature',
  );
  const awaitingBloomSpaSignature = t(
    'reserveUnit.reservationVisible.awaitingBloomSpaSignature',
  );
  const spaSigned = t('reserveUnit.reservationVisible.spaSigned');
  const reservationConfirmedListing = t(
    'reserveUnit.reservationVisible.reservationConfirmedListing',
  );
  const pendingBadge = t('reserveUnit.pendingBadge');

  return {
    [ReservationStatus.AWAITING_CUSTOMER_SIGNATURE]: {
      listing: pendingBadge,
      listingMessage: awaitingYourSignature,
      detailTitle: pendingBadge,
      detailMessage: awaitingYourSignature,
    },
    [ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM]: {
      listing: reservationConfirmedListing,
      listingMessage: bookingFormSigned,
      detailTitle: reservationConfirmedListing,
      detailMessage: bookingFormSigned,
    },
    [ReservationStatus.OPPORTUNITY_NOT_CREATED]: {
      listing: inProgressListing,
      listingMessage: reservationFormSent,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSent,
    },
    [ReservationStatus.OPPORTUNITY_CREATED_BEFORE_SALES_ORDER]: {
      listing: inProgressListing,
      listingMessage: reservationFormSent,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSent,
    },
    [ReservationStatus.AWAITING_RESERVATION_FORM_CUSTOMER]: {
      listing: inProgressListing,
      listingMessage: reservationFormSent,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSent,
    },
    [ReservationStatus.AWAITING_RESERVATION_FORM_BLOOM]: {
      listing: inProgressListing,
      listingMessage: reservationFormSent,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSent,
    },
    [ReservationStatus.RESERVATION_FORM_SIGNED]: {
      listing: inProgressListing,
      listingMessage: reservationFormSigned,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSigned,
    },
    [ReservationStatus.DECREE_APPROVAL]: {
      listing: inProgressListing,
      listingMessage: reservationFormSent,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSent,
    },
    [ReservationStatus.SPA_AWAITING_CUSTOMER]: {
      listing: inProgressListing,
      listingMessage: awaitingSpaSignature,
      detailTitle: inProgressListing,
      detailMessage: awaitingSpaSignature,
    },
    [ReservationStatus.SPA_AWAITING_BLOOM]: {
      listing: inProgressListing,
      listingMessage: awaitingBloomSpaSignature,
      detailTitle: inProgressListing,
      detailMessage: awaitingBloomSpaSignature,
    },
    [ReservationStatus.SPA_SIGNED]: {
      listing: inProgressListing,
      listingMessage: spaSigned,
      detailTitle: inProgressListing,
      detailMessage: spaSigned,
    },
    [ReservationStatus.UNKNOWN_RESERVED]: {
      listing: inProgressListing,
      listingMessage: reservationFormSent,
      detailTitle: inProgressListing,
      detailMessage: reservationFormSent,
    },
  } as const;
}

export function getUnitDetailsLabels(t: PropertyTranslateFn) {
  const reservationVisible = getReservationVisibleLabels(t);

  return {
    TITLE: t('propertyDetails.unitDetails'),
    HOME_BREADCRUMB: t('common.home'),
    ABOUT_UNIT: t('propertyDetails.aboutUnit'),
    UNIT_INFO: t('propertyDetails.unitInfo'),
    BEDROOMS: t('propertyDetails.bedrooms'),
    BATHROOMS: t('propertyDetails.bathrooms'),
    AREA_IN_SQFT: t('propertyDetails.areaInSqFt'),
    VIEW_FLOOR_PLAN: t('propertyDetails.viewFloorPlan'),
    AMENITIES: t('propertyDetails.amenities'),
    DOCUMENTS: t('propertyDetails.documents'),
    BROCHURE: t('propertyDetails.brochure'),
    MASTER_PLAN: t('propertyDetails.masterPlan'),
    VIEW: t('propertyDetails.view'),
    HANDOVER_DATE: t('propertyDetails.handoverDateWithDate'),
    CONSTRUCTION_COMPLETED: t('propertyDetails.constructionCompletedPercent'),
    UNIT_PRICE: t('propertyDetails.unitPriceLabel'),
    TOTAL_PRICE: t('propertyDetails.totalPrice'),
    OUTSTANDING_BALANCE: t('propertyDetails.outstandingBalance'),
    PAY_NOW: t('propertyDetails.payNow'),
    COMPLETE_PAYMENT_TITLE: t('propertyDetails.completePaymentTitle'),
    APPROVED_ISKAN_LOAN: t('propertyDetails.approvedIskanLoan'),
    HOW_TO_PROCEED: t('propertyDetails.howWouldYouLikeToProceed'),
    BANK_TRANSFER: t('propertyDetails.bankTransfer'),
    BANK_TRANSFER_DESCRIPTION: t('propertyDetails.bankTransferDescription'),
    PROOF_OF_MORTGAGE: t('propertyDetails.proofOfMortgage'),
    PROOF_OF_MORTGAGE_DESCRIPTION: t(
      'propertyDetails.proofOfMortgageDescription',
    ),
    BANK_TRANSFER_DETAILS_TITLE: t('propertyDetails.bankTransferDetailsTitle'),
    TRANSFER_TO_THIS_ACCOUNT: t('propertyDetails.transferToThisAccount'),
    ACCOUNT_NAME: t('propertyDetails.accountName'),
    AMOUNT_PAID: t('propertyDetails.amountPaid'),
    BANK_NAME: t('propertyDetails.bankName'),
    ACCOUNT_NUMBER: t('propertyDetails.accountNumber'),
    IBAN: t('propertyDetails.iban'),
    SWIFT_CODE: t('propertyDetails.swiftCode'),
    BANK_TRANSFER_NOTE_PREFIX: t('propertyDetails.bankTransferNotePrefix'),
    IVE_MADE_THE_TRANSFER: t('propertyDetails.iveMadeTheTransfer'),
    COPIED_TO_CLIPBOARD: t('propertyDetails.copiedToClipboard'),
    SUBMISSION_RECEIVED_TITLE: t('propertyDetails.submissionReceivedTitle'),
    SUBMIT_PROOF_PAYMENT_INSTRUCTION: t(
      'propertyDetails.submitProofPaymentInstruction',
    ),
    SUBMIT_PROOF_MORTGAGE_INSTRUCTION: t(
      'propertyDetails.submitProofMortgageInstruction',
    ),
    SUBMIT_PROOF_REVIEW_NOTE: t('propertyDetails.submitProofReviewNote'),
    SUBMIT_PROOF_EMAIL: t('propertyDetails.submitProofEmail'),
    BACK_TO_UNIT_DETAILS: t('propertyDetails.backToUnitDetails'),
    CONTINUE: t('common.continue'),
    BACK: t('common.back'),
    ISKAN_LOAN_TITLE: t('reserveUnit.iskanLoan'),
    ISKAN_LOAN_COVERS: t('propertyDetails.iskanLoanCoversTemplate'),
    ISKAN_LOAN_COVERS_VERB: t('propertyDetails.iskanLoanCoversVerb'),
    ISKAN_LOAN_COVERS_REST: t('propertyDetails.iskanLoanCoversUnitSuffix'),
    BOOK_APPOINTMENT: t('propertyDetails.bookAppointmentTitle'),
    BOOK_APPOINTMENT_BUTTON: t('bookAppointment.bookAppointment'),
    BOOK_APPOINTMENT_DESCRIPTION: t('propertyDetails.bookAppointmentDescription'),
    RESERVE_UNIT: t('propertyDetails.reserveUnitTitle'),
    RESERVE_UNIT_DESCRIPTION: t('propertyDetails.reserveUnitDescription'),
    VIEW_APPOINTMENT: t('propertyDetails.viewAppointmentCta'),
    CHOOSE_NEXT_STEP: t('propertyDetails.chooseNextStep'),
    CLOSE: t('common.close'),
    UNIT_NOT_FOUND: t('reserveUnit.unitAlreadyReservedError'),
    UNIT_LOAD_ERROR: t('propertyDetails.unitLoadError'),
    TOAST_DOWNLOAD_FAILED: t('propertyDetails.documentDownloadFailed'),
    TOAST_UNIT_UNAVAILABLE: t('reserveUnit.unitAlreadyReservedError'),
    TOAST_ACTIVE_RESERVATION: t('reserveUnit.activeReservationToast'),
    TOAST_ACTIVE_RESERVATION_LISTING: t(
      'reserveUnit.activeReservationListingToast',
    ),
    TOAST_RESERVE_FAILED: t('reserveUnit.submissionFailedError'),
    TOAST_CANCEL_FAILED: t('reserveUnit.cancelReservationError'),
    CONFIRM_RESERVATION_TITLE: t('reserveUnit.confirmTitle'),
    CONFIRM_RESERVATION_SUBTITLE: t('reserveUnit.confirmSubtitle'),
    TOTAL_UNIT_PRICE: t('reserveUnit.totalUnitPrice'),
    ISKAN_LOAN: t('reserveUnit.iskanLoan'),
    REMAINING_BALANCE: t('reserveUnit.remainingBalance'),
    IMPORTANT_TO_NOTE: t('reserveUnit.importantToNote'),
    IMPORTANT_NOTE_BODY: t('reserveUnit.importantNoteBody'),
    WHAT_HAPPENS_NEXT: t('reserveUnit.whatHappensNext'),
    NEXT_STEP_EMAIL: t('reserveUnit.stepEmail'),
    NEXT_STEP_DEADLINE: t('reserveUnit.stepDeadline'),
    CONFIRM_CANCEL: t('common.back'),
    CONFIRM_CANCEL_SHORTFALL: t('common.cancel'),
    CONFIRM_PROCEED: t('reserveUnit.proceedCta'),
    CONFIRM_PROCEED_SHORTFALL: t('reserveUnit.proceedShortfallCta'),
    HANDOVER_PREFIX: t('propertyDetails.handover'),
    UNIT_RESERVED_TITLE: t('reserveUnit.unitReservedTitle'),
    UNIT_RESERVED_CONGRATS: t('reserveUnit.unitReservedCongrats'),
    UNIT_RESERVED_MESSAGE: t('reserveUnit.unitReservedMessage'),
    UNIT_RESERVED_OK: t('common.ok'),
    CANCEL_RESERVATION_TITLE: t('reserveUnit.cancelReservation'),
    CANCEL_RESERVATION_MESSAGE: t('reserveUnit.cancelReservationConfirmMessage'),
    CANCEL_RESERVATION_NO: t('common.no'),
    CANCEL_RESERVATION_YES: t('common.yes'),
    CANCEL_RESERVATION_BUTTON: t('common.cancel'),
    RESERVATION_CANCELED_TITLE: t('reserveUnit.reservationCanceledTitle'),
    RESERVATION_CANCELED_MESSAGE: t('reserveUnit.reservationCanceledMessage'),
    RESERVATION_PENDING: t('reserveUnit.reservationPending'),
    RESERVATION_IN_PROGRESS: t('reserveUnit.reservationInProgress'),
    RESERVATION_CONFIRMED: t('reserveUnit.reservationConfirmed'),
    RESERVATION_AWAITING_SIGNATURE: t('reserveUnit.awaitingSignatureNote'),
    RESERVATION_STATUS_DISPLAY: {
      [ReservationStatus.AWAITING_CUSTOMER_SIGNATURE]: t(
        'reserveUnit.awaitingSignatureNote',
      ),
      [ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM]: t(
        'reserveUnit.reservationStatus.customerSignedBookingForm',
      ),
      [ReservationStatus.OPPORTUNITY_NOT_CREATED]: t(
        'reserveUnit.reservationStatus.opportunityNotCreated',
      ),
      [ReservationStatus.OPPORTUNITY_CREATED_BEFORE_SALES_ORDER]: t(
        'reserveUnit.reservationStatus.opportunityCreated',
      ),
      [ReservationStatus.AWAITING_RESERVATION_FORM_CUSTOMER]: t(
        'reserveUnit.reservationStatus.awaitingReservationFormCustomer',
      ),
      [ReservationStatus.AWAITING_RESERVATION_FORM_BLOOM]: t(
        'reserveUnit.reservationStatus.awaitingReservationFormBloom',
      ),
      [ReservationStatus.RESERVATION_FORM_SIGNED]: t(
        'reserveUnit.reservationStatus.reservationFormSigned',
      ),
      [ReservationStatus.DECREE_APPROVAL]: t(
        'reserveUnit.reservationStatus.decreeApproval',
      ),
      [ReservationStatus.SPA_AWAITING_CUSTOMER]: t(
        'reserveUnit.reservationStatus.spaAwaitingCustomer',
      ),
      [ReservationStatus.SPA_AWAITING_BLOOM]: t(
        'reserveUnit.reservationStatus.spaAwaitingBloom',
      ),
      [ReservationStatus.SPA_SIGNED]: t(
        'reserveUnit.reservationStatus.spaSigned',
      ),
      [ReservationStatus.UNKNOWN_RESERVED]: t(
        'reserveUnit.reservationStatus.unknownReserved',
      ),
    },
    RESERVATION_VISIBLE: reservationVisible,
    VIEW_RESERVATION: t('propertyDetails.viewReservationCta'),
    RESERVED_BANNER_TITLE: t('reserveUnit.reservedBannerTitle'),
    RESERVATION_SUMMARY: t('reserveUnit.reservationSummaryTitle'),
    REQUESTED_ON: t('reserveUnit.requestedOn'),
    RESERVATION_STATUS: t('reserveUnit.status'),
  } as const;
}

export type UnitDetailsLabels = ReturnType<typeof getUnitDetailsLabels>;

export function getPropertyLabels(t: PropertyTranslateFn) {
  return {
    TITLE: t('propertiesWeb.title'),
    DETAILS: t('propertyDetails.title'),
    UNIT_DETAILS: getUnitDetailsLabels(t),
    listing: getListingLabels(t),
  } as const;
}

export type PropertyLabels = ReturnType<typeof getPropertyLabels>;
